import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import SecureRoute from './components/SecureRoute';
import './components/i18n';
//main imports
import Home from './pages/Home';
import About  from './pages/About';
import UniversityServices from './pages/UniversityServices';
import Contact from './pages/Contact';
import Login from './pages/Login';
import TryAI from './pages/TryAI'
import Universities from './pages/Universities';
import UniversityInfo from './pages/UniversityInfo';
import UniversityBook from './pages/UniversityBook';
import UniversityConfirmation from './pages/UniversityConfirmation';
import Unauthorized from './pages/Unauthorized';
import Profile from './components/Profile'
import ForgotPassword from './components/ForgotPassword';
import Support from './components/Support';
//student
import StudentDashboard from './student/Dashboard';
import StudentCalendar from './student/Calendar';
import StudentPatients from './student/Patients';
import StudentAnalytics from './student/Analytics';
import StudentReviews from './student/Reviews';
import StudentPatientProfile from './student/PatientProfile';
import StudentGallery from './student/Gallery';
// import StudentXRay from './student/XRay'; // No longer needed after merging with Gallery
import StudentToothChart from './student/ToothChart';
import StudentAppointments from './student/Appointments';
import StudentReviewSteps from './student/ReviewSteps'
import StudentSheets from './student/Sheets'
import StudentHistory from './student/History'
import StudentConsent from './student/Consent'
import StudentLab from './student/Lab'
import StudentPatientLab from './student/PatientLab'
import StudentSubmittedReviews from './student/SubmittedReviews'
import StudentMedical from './student/Medical'
import StudentPatientCard from './student/PatientCard'
//supervisor
import SupervisorLayout from './supervisor/SupervisorLayout';
import SupervisorDashboard from './supervisor/SupervisorDashboard';
import SupervisorReviews from './supervisor/Reviews';
import SupervisorStudents from './supervisor/Students';
import SupervisorAnalytics from './supervisor/Analytics';
import SupervisorReports from './supervisor/Reports';
import SupervisorPatientInfo from './supervisor/SupervisorPatientInfo';
import SupervisorLabRequests from './supervisor/LabRequests';
//admin
import AdminDashboard from './admin/Dashboard';
import AdminPeople from './admin/People';
import AdminAppointments from './admin/Appointments';
import AdminAnalytics from './admin/Analytics';
import AdminNews from './admin/News';
import AdminReviews from './admin/Reviews';
import AdminLabRequests from './admin/LabRequests';
import StudentActivityDashboard from './admin/StudentActivityDashboard';
import EditTrackingDashboard from './admin/EditTrackingDashboard';
import ComprehensiveTrackingDashboard from './admin/ComprehensiveTrackingDashboard';
//superadmin
import SuperadminDashboard from './superadmin/Dashboard';
import SuperadminUniversities from './superadmin/Universities';
import SuperadminClinics from './superadmin/Clinics';
import SuperadminAnalytics from './superadmin/Analytics';
import SuperadminNews from './superadmin/News';
import SuperadminAccounts from './superadmin/Accounts';
import SuperadminActivity from './superadmin/Activity';
import ImpersonationCallback from './superadmin/ImpersonationCallback';
//dentist
import DentistDashboard from './dentist/Dashboard';
import DentistCalendar from './dentist/Calendar';
import DentistPatients from './dentist/Patients';
import DentistMessages from './dentist/Messages';
import DentistAnalytics from './dentist/Analytics';
import DentistNotifications from './dentist/Notifications';
import DentistPatientProfile from './dentist/PatientProfile';
import DentistGallery from './dentist/Gallery';
import DentistXRay from './dentist/XRay';
import DentistToothChart from './dentist/ToothChart';
import DentistAppointments from './dentist/Appointments';
import DentistMedical from './dentist/Medical';
import DentistHistory from './dentist/History';
import DentistTreatmentPlan from './dentist/TreatmentPlan';
import DentistInvoice from './dentist/Invoice';
import DentistPrescription from './dentist/Prescription';
//assistant
import AssistantDashboard from './assistant/Dashboard';
import AssistantAppointments from './assistant/Appointments';
import AssistantAnalytics from './assistant/Analytics';
import AssistantProcedureRequests from './assistant/ProcedureRequests';
import AssistantPatients from './assistant/Patients';
import AssistantLabRequests from './assistant/LabRequests';
import AssistantPatientCard from './assistant/PatientCard';
//it
import ITDashboard from './it/Dashboard';
import Accounts from './it/Accounts';
import BulkCreate from './it/BulkCreate';
import DebugIT from './it/DebugIT';


// Generic ProtectedRoute component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Role-specific wrappers
const StudentRoute = ({ children }) => <ProtectedRoute><StudentCheck>{children}</StudentCheck></ProtectedRoute>;
const SupervisorRoute = ({ children }) => <ProtectedRoute><SupervisorCheck>{children}</SupervisorCheck></ProtectedRoute>;
const AdminRoute = ({ children }) => <ProtectedRoute><AdminCheck>{children}</AdminCheck></ProtectedRoute>;
const SuperadminRoute = ({ children }) => <ProtectedRoute><SuperadminCheck>{children}</SuperadminCheck></ProtectedRoute>;
const AssistantRoute = ({ children }) => <ProtectedRoute><AssistantCheck>{children}</AssistantCheck></ProtectedRoute>;
const DentistRoute = ({ children }) => <ProtectedRoute><DentistCheck>{children}</DentistCheck></ProtectedRoute>;
const ITRoute = ({ children }) => <ProtectedRoute><ITCheck>{children}</ITCheck></ProtectedRoute>;

// Role-specific checks
const StudentCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'student' ? children : <Navigate to="/dashboard" replace />;
};
const SupervisorCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'supervisor' ? children : <Navigate to="/dashboard" replace />;
};
const AdminCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
};
const SuperadminCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'superadmin' ? children : <Navigate to="/dashboard" replace />;
};
const AssistantCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'assistant' ? children : <Navigate to="/dashboard" replace />;
};
const DentistCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'dentist' ? children : <Navigate to="/dashboard" replace />;
};
const ITCheck = ({ children }) => {
  const { user } = useAuth();
  return user.role === 'it' ? children : <Navigate to="/dashboard" replace />;
};

// Dynamic dashboard redirect based on role
const DashboardRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case 'student':
      return <Navigate to="/student/dashboard" replace />;
    case 'supervisor':
      return <Navigate to="/supervisor/dashboard" replace />;
    case 'admin':
      return <Navigate to="/admin/dashboard" replace />;
    case 'superadmin':
      return <Navigate to="/superadmin/dashboard" replace />;
    case 'assistant':
      return <Navigate to="/assistant/dashboard" replace />;
    case 'dentist':
      return <Navigate to="/dentist/dashboard" replace />;
    case 'it':
      return <Navigate to="/it/dashboard" replace />;

    default:
      return <Navigate to="/" replace />; // Fallback for unexpected roles
  }
};

const AppRoutes = () => (
  <Routes>
    {/* Public Routes */}
    <Route path="/" element={<Home />} />
      {/* About page locked — redirect to home; re-enable later by using element={<About />} */}
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/universityServices" element={<UniversityServices />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route path="/try-ai" element={<TryAI />} />
      <Route path="/universities" element={<Universities />} />
      <Route path="/university-info/:universityId" element={<UniversityInfo />} />
      <Route path="/university-book" element={<UniversityBook />} />
      <Route path="/university-confirmation" element={<UniversityConfirmation />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/unauthorized" element={<Unauthorized />} />


    {/* Dynamic Dashboard Redirect */}
    <Route path="/dashboard" element={<DashboardRedirect />} />

    {/* Student Routes */}
    <Route path="/student/dashboard" element={<StudentRoute><StudentDashboard /></StudentRoute>} />
    <Route path="/calendar" element={<StudentRoute><StudentCalendar /></StudentRoute>} />
    <Route path="/patients" element={<StudentRoute><StudentPatients /></StudentRoute>} />
    <Route path="/patient-cards" element={<StudentRoute><StudentPatientCard /></StudentRoute>} />
    {/* <Route path="/messages" element={<StudentRoute><StudentMessages /></StudentRoute>} /> */}
    <Route path="/analytics" element={<StudentRoute><StudentAnalytics /></StudentRoute>} />
    <Route path="/reviews" element={<StudentRoute><StudentReviews /></StudentRoute>} />
    <Route path="/lab" element={<StudentRoute><StudentLab /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId" element={<StudentRoute><StudentPatientProfile /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/gallery" element={<StudentRoute><StudentGallery /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/xrays" element={<Navigate to="../gallery" replace />} />
    <Route path="/patientprofile/:nationalId/toothchart" element={<StudentRoute><StudentToothChart /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/appointments" element={<StudentRoute><StudentAppointments /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/reviewsteps" element={<StudentRoute><StudentReviewSteps /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/sheets" element={<StudentRoute><StudentSheets /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/medical" element={<StudentRoute><StudentMedical /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/history" element={<StudentRoute><StudentHistory /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/consent" element={<StudentRoute><StudentConsent /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/lab" element={<StudentRoute><StudentPatientLab /></StudentRoute>} />
    <Route path="/patientprofile/:nationalId/submitted-reviews" element={<StudentRoute><StudentSubmittedReviews /></StudentRoute>} />

    <Route path="/profile" element={<Profile />} />
    <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />



    {/* Supervisor Routes */}
    <Route path="/supervisor" element={<SecureRoute requiredRole="supervisor"><SupervisorLayout /></SecureRoute>}>
      <Route path="dashboard" element={<SupervisorDashboard />} />
      <Route path="reviews" element={<SupervisorReviews />} />
      <Route path="students" element={<SupervisorStudents />} />
      <Route path="lab-requests" element={<SupervisorLabRequests />} />
      <Route path="analytics" element={<SupervisorAnalytics />} />
      <Route path="reports" element={<SupervisorReports />} />
      <Route path="patient/:nationalId" element={<SupervisorPatientInfo />} />
    </Route>


    {/* Admin Routes */}
    <Route path="/admin/dashboard" element={<SecureRoute requiredRole="admin"><AdminDashboard /></SecureRoute>} />
    <Route path="/admin/people" element={<SecureRoute requiredRole="admin"><AdminPeople /></SecureRoute>} />
    <Route path="/admin/appointments" element={<SecureRoute requiredRole="admin"><AdminAppointments /></SecureRoute>} />
    <Route path="/admin/analytics" element={<SecureRoute requiredRole="admin"><AdminAnalytics /></SecureRoute>} />
    <Route path="/admin/news" element={<SecureRoute requiredRole="admin"><AdminNews /></SecureRoute>} />
    <Route path="/admin/reviews" element={<SecureRoute requiredRole="admin"><AdminReviews /></SecureRoute>} />
    <Route path="/admin/lab-requests" element={<SecureRoute requiredRole="admin"><AdminLabRequests /></SecureRoute>} />
    <Route path="/admin/student-activity" element={<SecureRoute requiredRole="admin"><StudentActivityDashboard /></SecureRoute>} />
    <Route path="/admin/edit-tracking" element={<SecureRoute requiredRole="admin"><EditTrackingDashboard /></SecureRoute>} />
    <Route path="/admin/comprehensive-tracking" element={<SecureRoute requiredRole="admin"><ComprehensiveTrackingDashboard /></SecureRoute>} />

     {/* SuperAdmin Routes */}
    <Route path="/superadmin/dashboard" element={<SecureRoute requiredRole="superadmin"><SuperadminDashboard /></SecureRoute>} />
    <Route path="/superadmin/universities" element={<SecureRoute requiredRole="superadmin"><SuperadminUniversities /></SecureRoute>} />
    <Route path="/superadmin/clinics" element={<SecureRoute requiredRole="superadmin"><SuperadminClinics /></SecureRoute>} />
    <Route path="/superadmin/analytics" element={<SecureRoute requiredRole="superadmin"><SuperadminAnalytics /></SecureRoute>} />
    <Route path="/superadmin/activity" element={<SecureRoute requiredRole="superadmin"><SuperadminActivity /></SecureRoute>} />
    <Route path="/superadmin/news" element={<SecureRoute requiredRole="superadmin"><SuperadminNews /></SecureRoute>} />
    <Route path="/superadmin/accounts" element={<SecureRoute requiredRole="superadmin"><SuperadminAccounts /></SecureRoute>} />

    {/* Landing page for a tab opened by the superadmin role switcher.
        Deliberately unguarded: this tab has no session yet - the single-use
        code in the URL is what it trades for one. */}
    <Route path="/impersonate" element={<ImpersonationCallback />} />

    {/* Dentist Routes */}
    <Route path="/dentist/dashboard" element={<DentistRoute><DentistDashboard /></DentistRoute>} />
    <Route path="/dentist/calendar" element={<DentistRoute><DentistCalendar /></DentistRoute>} />
    <Route path="/dentist/patients" element={<DentistRoute><DentistPatients /></DentistRoute>} />
    <Route path="/dentist/messages" element={<DentistRoute><DentistMessages /></DentistRoute>} />
            <Route path="/dentist/analytics" element={<DentistRoute><DentistAnalytics /></DentistRoute>} />
        <Route path="/dentist/notifications" element={<DentistRoute><DentistNotifications /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId" element={<DentistRoute><DentistPatientProfile /></DentistRoute>} />
    <Route path="/dentist/patientprofile/:nationalId/gallery" element={<DentistRoute><DentistGallery /></DentistRoute>} />
    <Route path="/dentist/patientprofile/:nationalId/xrays" element={<DentistRoute><DentistXRay /></DentistRoute>} />
            <Route path="/dentist/patientprofile/:nationalId/toothchart" element={<DentistRoute><DentistToothChart /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/appointments" element={<DentistRoute><DentistAppointments /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/medical" element={<DentistRoute><DentistMedical /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/history" element={<DentistRoute><DentistHistory /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/treatment-plan" element={<DentistRoute><DentistTreatmentPlan /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/invoice" element={<DentistRoute><DentistInvoice /></DentistRoute>} />
        <Route path="/dentist/patientprofile/:nationalId/prescription" element={<DentistRoute><DentistPrescription /></DentistRoute>} />

    {/* Assistant Routes */}
    <Route path="/assistant/dashboard" element={<AssistantRoute><AssistantDashboard /></AssistantRoute>} />
    <Route path="/assistant/appointments" element={<AssistantRoute><AssistantAppointments /></AssistantRoute>} />
    <Route path="/assistant/procedure-requests" element={<AssistantRoute><AssistantProcedureRequests /></AssistantRoute>} />
    <Route path="/assistant/lab-requests" element={<AssistantRoute><AssistantLabRequests /></AssistantRoute>} />
    <Route path="/assistant/analytics" element={<AssistantRoute><AssistantAnalytics /></AssistantRoute>} />
    <Route path="/assistant/patients" element={<AssistantRoute><AssistantPatients /></AssistantRoute>} />
    <Route path="/assistant/patient-cards" element={<AssistantRoute><AssistantPatientCard /></AssistantRoute>} />

    {/* IT Routes */}
    <Route path="/it/dashboard" element={<ITRoute><ITDashboard /></ITRoute>} />
    <Route path="/it/accounts" element={<ITRoute><Accounts /></ITRoute>} />
    <Route path="/it/bulk-create" element={<ITRoute><BulkCreate /></ITRoute>} />
    <Route path="/it/debug" element={<DebugIT />} />

    {/* 404 */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRoutes;
